<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { errorMessage } from '@/utils/format';

/**
 * 按人力资源架构树选人。
 *
 * 试用反馈 0814：「设置人力资源架构树，团队成员可选择」——原来选人只有一个
 * 打字筛选的平铺下拉，不知道这个人属于哪个部门，也没法按部门找。
 *
 * 一个不能忽视的事实：库里 183 个部门只有 28 个有人，155 个是旧 EQS 迁来的空壳。
 * 所以**默认只展开有人的部门**，空的折叠起来单独说明——把 183 个节点原样铺出来，
 * 找人比原来的下拉还慢。
 */
interface Member { id: number; displayName: string; username: string | null; email: string | null }
interface Node {
  id: number; name: string; company: string | null; parentId: number | null;
  memberCount: number; totalMemberCount: number; members: Member[]; children: Node[];
}

const props = defineProps<{ modelValue: number | null }>();
const emit = defineEmits<{ 'update:modelValue': [value: number | null, member: Member | null] }>();

const loading = ref(true);
const error = ref('');
const roots = ref<Node[]>([]);
const unassigned = ref<Member[]>([]);
const totalUnits = ref(0);
const emptyUnits = ref(0);
const totalMembers = ref(0);
const query = ref('');
const expanded = ref<Set<number>>(new Set());
const showEmpty = ref(false);

onMounted(async () => {
  try {
    const payload = await eqsApi.orgTree();
    roots.value = (payload.roots ?? []) as unknown as Node[];
    unassigned.value = (payload.unassigned ?? []) as unknown as Member[];
    totalUnits.value = Number(payload.totalUnits ?? 0);
    emptyUnits.value = Number(payload.emptyUnits ?? 0);
    totalMembers.value = Number(payload.totalMembers ?? 0);
    // 有人的部门默认展开——28 个而已，一眼能看完
    const open = new Set<number>();
    const walk = (nodes: Node[]): void => {
      for (const node of nodes) {
        if (node.totalMemberCount > 0) open.add(node.id);
        walk(node.children);
      }
    };
    walk(roots.value);
    expanded.value = open;
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
});

/** 搜索时忽略折叠状态，直接把命中的人摊平列出来——找人比看结构重要 */
const matches = computed<Member[]>(() => {
  const needle = query.value.trim().toLowerCase();
  if (!needle) return [];
  const hits: Member[] = [];
  const walk = (nodes: Node[]): void => {
    for (const node of nodes) {
      for (const member of node.members) {
        const hay = `${member.displayName} ${member.username ?? ''} ${member.email ?? ''} ${node.name}`.toLowerCase();
        if (hay.includes(needle)) hits.push(member);
      }
      walk(node.children);
    }
  };
  walk(roots.value);
  for (const member of unassigned.value) {
    if (`${member.displayName} ${member.username ?? ''}`.toLowerCase().includes(needle)) hits.push(member);
  }
  return hits.slice(0, 40);
});

function visibleChildren(node: Node): Node[] {
  return node.children.filter((child) => showEmpty.value || child.totalMemberCount > 0);
}
const visibleRoots = computed(() => roots.value.filter((node) => showEmpty.value || node.totalMemberCount > 0));

function toggle(id: number): void {
  const next = new Set(expanded.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expanded.value = next;
}

function pick(member: Member): void {
  emit('update:modelValue', member.id, member);
}

function memberHint(member: Member): string {
  return [member.username, member.email].filter(Boolean).join(' · ');
}
</script>

<template>
  <div class="org-tree">
    <input v-model="query" type="search" class="org-search" placeholder="搜姓名、工号、邮箱或部门">

    <p v-if="loading" class="org-hint">正在读取组织架构…</p>
    <p v-else-if="error" class="org-hint org-error">读取失败：{{ error }}</p>

    <template v-else>
      <ul v-if="query.trim()" class="org-matches">
        <li v-for="member in matches" :key="member.id">
          <button type="button" :class="{ on: member.id === props.modelValue }" @click="pick(member)">
            <b>{{ member.displayName }}</b><small>{{ memberHint(member) }}</small>
          </button>
        </li>
        <li v-if="!matches.length" class="org-hint">没有匹配的人。</li>
      </ul>

      <template v-else>
        <ul class="org-nodes">
          <li v-for="node in visibleRoots" :key="node.id">
            <button type="button" class="org-node" @click="toggle(node.id)">
              <i>{{ expanded.has(node.id) ? '▾' : '▸' }}</i>
              <b>{{ node.name }}</b>
              <small>{{ node.totalMemberCount }} 人</small>
            </button>
            <template v-if="expanded.has(node.id)">
              <ul class="org-members">
                <li v-for="member in node.members" :key="member.id">
                  <button type="button" :class="{ on: member.id === props.modelValue }" @click="pick(member)">
                    <b>{{ member.displayName }}</b><small>{{ memberHint(member) }}</small>
                  </button>
                </li>
              </ul>
              <ul v-if="visibleChildren(node).length" class="org-nodes org-sub">
                <li v-for="child in visibleChildren(node)" :key="child.id">
                  <button type="button" class="org-node" @click="toggle(child.id)">
                    <i>{{ expanded.has(child.id) ? '▾' : '▸' }}</i>
                    <b>{{ child.name }}</b>
                    <small>{{ child.totalMemberCount }} 人</small>
                  </button>
                  <ul v-if="expanded.has(child.id)" class="org-members">
                    <li v-for="member in child.members" :key="member.id">
                      <button type="button" :class="{ on: member.id === props.modelValue }" @click="pick(member)">
                        <b>{{ member.displayName }}</b><small>{{ memberHint(member) }}</small>
                      </button>
                    </li>
                  </ul>
                </li>
              </ul>
            </template>
          </li>
        </ul>

        <ul v-if="unassigned.length" class="org-members org-unassigned">
          <li class="org-hint">未归部门（{{ unassigned.length }} 人）</li>
          <li v-for="member in unassigned" :key="member.id">
            <button type="button" :class="{ on: member.id === props.modelValue }" @click="pick(member)">
              <b>{{ member.displayName }}</b><small>{{ memberHint(member) }}</small>
            </button>
          </li>
        </ul>
      </template>

      <p class="org-foot">
        {{ totalMembers }} 人 · {{ totalUnits - emptyUnits }} 个部门有人
        <template v-if="emptyUnits">
          ，另有 {{ emptyUnits }} 个空部门已折叠
          <button type="button" class="org-toggle" @click="showEmpty = !showEmpty">
            {{ showEmpty ? '收起空部门' : '也显示空部门' }}
          </button>
        </template>
      </p>
    </template>
  </div>
</template>

<style scoped>
.org-tree { display: grid; gap: 8px; padding: 10px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); }
.org-search { padding: 6px 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.org-hint { margin: 0; color: var(--muted); font-size: 12px; }
.org-error { color: var(--risk); }

.org-nodes, .org-members, .org-matches { margin: 0; padding: 0; list-style: none; display: grid; gap: 2px; }
.org-nodes { max-height: 300px; overflow: auto; }
.org-sub { margin-left: 14px; max-height: none; }
.org-members { margin-left: 18px; }
.org-unassigned { margin-left: 0; padding-top: 6px; border-top: 1px dashed var(--line); }

.org-node, .org-members button, .org-matches button {
  display: flex; align-items: baseline; gap: 7px; width: 100%; padding: 4px 8px;
  border: 1px solid transparent; border-radius: 4px; background: transparent;
  cursor: pointer; text-align: left; font: inherit;
}
.org-node:hover, .org-members button:hover, .org-matches button:hover { background: #f2f6fb; }
.org-members button.on, .org-matches button.on { border-color: var(--blue); background: rgba(0, 117, 174, .1); }
.org-node i { color: var(--muted); font-style: normal; font-size: 11px; }
.org-node b { font-size: 12px; }
.org-node small, .org-members small, .org-matches small { color: var(--muted); font-size: 11px; }
.org-members b, .org-matches b { font-size: 12px; font-weight: 400; }

.org-foot { margin: 0; padding-top: 6px; border-top: 1px solid var(--line); color: var(--muted); font-size: 11px; line-height: 1.7; }
.org-toggle { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); font-size: 11px; }
.org-toggle:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }
</style>
